import { useState } from 'react';
import {
  type DayCloseSessionRow,
  type DayCloseSubmissionView,
  ENUM_LABELS,
  formatPersonName,
} from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import type { Column } from '#client/components/ui/DataTable';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Modal } from '#client/components/ui/Modal';
import { DayCloseStatusBadge } from '#client/components/ui/StatusBadge';
import { TextArea } from '#client/components/ui/TextArea';
import { useCan } from '#client/hooks/useCan';
import { useMutation } from '#client/hooks/useMutation';
import { formatDateTime, formatMoney, dash } from '#client/lib/format';
import { useToast } from '#client/providers/ToastProvider';
import { SummaryItem } from './DrawerFigures';
import { hasDrifted } from './day-close.logic';
import { reviewDayClose } from './day-close.service';
import { DAY_CLOSE_REMARK_MAX_LENGTH } from '#shared/types';

/**
 * Every login on the day, with what each staff member submitted.
 *
 * The reconciliation view: the snapshot a staff member signed off beside the
 * session's figures as they stand now. They drift when a visit from that
 * session is edited or refunded afterwards, and that drift is precisely what
 * the admin needs to see before calling the day closed.
 */
export function DayCloseSessions({
  sessions,
  onReviewed,
}: {
  sessions: DayCloseSessionRow[];
  onReviewed: () => void;
}) {
  const can = useCan();
  const [reviewing, setReviewing] = useState<DayCloseSessionRow | null>(null);
  const reviews = can('dayClose.review');

  const columns = reviews ? [...SESSION_COLUMNS, reviewColumn(setReviewing)] : SESSION_COLUMNS;

  return (
    <Card title="Sessions" flush>
      {sessions.length === 0 ? (
        <EmptyState title="Nobody signed in" body="No login sessions overlap this day." />
      ) : (
        <DataTable
          columns={columns}
          rows={sessions}
          rowKey={(row) => row.id}
          caption="Login sessions on this day and their Day Close submissions"
        />
      )}
      {reviewing?.submission && (
        <ReviewModal
          session={reviewing}
          submission={reviewing.submission}
          onClose={() => setReviewing(null)}
          onReviewed={() => {
            setReviewing(null);
            onReviewed();
          }}
        />
      )}
    </Card>
  );
}

const SESSION_COLUMNS: Column<DayCloseSessionRow>[] = [
  {
    key: 'user',
    header: 'Staff',
    frozen: true,
    render: (row) => formatPersonName(row.user),
  },
  {
    key: 'window',
    header: 'Session',
    width: '260px',
    render: (row) => <SessionWindow row={row} />,
  },
  {
    key: 'registrations',
    header: 'Visits',
    width: '80px',
    align: 'end',
    render: (row) => <span className="tnum">{row.live.registrations}</span>,
  },
  {
    key: 'billed',
    header: 'Billed',
    width: '140px',
    align: 'end',
    render: (row) => <span className="tnum">{formatMoney(row.live.billed)}</span>,
  },
  {
    key: 'collected',
    header: 'Collected',
    width: '140px',
    align: 'end',
    render: (row) => <span className="tnum">{formatMoney(row.live.collected)}</span>,
  },
  {
    key: 'status',
    header: 'Day Close',
    width: '280px',
    render: (row) => <SessionStatus row={row} />,
  },
  {
    key: 'comment',
    header: 'Comment',
    render: (row) => dash(row.submission?.comment),
  },
];

/** The trailing column an admin reconciles from. */
function reviewColumn(onReview: (row: DayCloseSessionRow) => void): Column<DayCloseSessionRow> {
  return {
    key: 'review',
    header: <span className="sr-only">Reconcile</span>,
    width: '130px',
    align: 'end',
    render: (row) =>
      row.submission && (
        <Button size="sm" onClick={() => onReview(row)}>
          {row.submission.reviewed_at ? 'Review' : 'Reconcile'}
        </Button>
      ),
  };
}

function SessionWindow({ row }: { row: DayCloseSessionRow }) {
  return (
    <>
      {formatDateTime(row.started_at)} – {row.ended_at ? formatDateTime(row.ended_at) : 'now'}
      {row.end_reason && row.end_reason !== 'submitted' && (
        <span className="c-day-close__note">{ENUM_LABELS.sessionEndReason[row.end_reason]}</span>
      )}
    </>
  );
}

function SessionStatus({ row }: { row: DayCloseSessionRow }) {
  return (
    <>
      <DayCloseStatusBadge status={row.status} />
      {row.submission && hasDrifted(row) && (
        <span className="c-day-close__note c-money--watch">Changed since submitting</span>
      )}
      {row.submission?.reviewed_at && <span className="c-day-close__note">Reconciled</span>}
    </>
  );
}

function ReviewModal({
  session,
  submission,
  onClose,
  onReviewed,
}: {
  session: DayCloseSessionRow;
  submission: DayCloseSubmissionView;
  onClose: () => void;
  onReviewed: () => void;
}) {
  const toast = useToast();
  const [note, setNote] = useState(submission.review_note ?? '');
  const review = useMutation('The review was not saved.');
  const { snapshot } = submission;

  async function handleSave() {
    const outcome = await review.run(() =>
      reviewDayClose(submission.id, { note: note.trim() || null }),
    );
    if (!outcome.ok) return;
    toast.success(`${formatPersonName(session.user)}'s Day Close is reconciled`);
    onReviewed();
  }

  return (
    <Modal
      open
      title={`Reconcile ${formatPersonName(session.user)}`}
      onClose={review.isPending ? () => undefined : onClose}
      primaryAction={{
        label: 'Mark reconciled',
        onAction: () => void handleSave(),
        loading: review.isPending,
      }}
      secondaryAction={{ label: 'Cancel', onAction: onClose }}
    >
      <div className="c-form-stack">
        <p>Submitted {formatDateTime(submission.submitted_at)}.</p>
        <dl className="c-summary">
          <Compare
            label="Visits"
            then={String(snapshot.registrations)}
            now={String(session.live.registrations)}
          />
          <Compare
            label="Billed"
            then={formatMoney(snapshot.billed)}
            now={formatMoney(session.live.billed)}
          />
          <Compare
            label="Collected"
            then={formatMoney(snapshot.collected)}
            now={formatMoney(session.live.collected)}
          />
          <SummaryItem
            label="Outstanding at submission"
            value={formatMoney(snapshot.outstanding)}
          />
        </dl>
        <div>
          <strong>Their comment</strong>
          <p>{submission.comment ?? 'No comment.'}</p>
        </div>
        <TextArea
          label="Review note"
          helpText="Optional. What you checked, or what was agreed."
          value={note}
          maxLength={DAY_CLOSE_REMARK_MAX_LENGTH}
          onChange={(event) => setNote(event.target.value)}
        />
        {review.errorMessage && <Banner tone="critical">{review.errorMessage}</Banner>}
      </div>
    </Modal>
  );
}

/** A submitted figure, with the live one beside it only when they differ. */
function Compare({ label, then, now }: { label: string; then: string; now: string }) {
  return (
    <SummaryItem
      label={label}
      value={then === now ? then : `${then} submitted · ${now} now`}
      tone={then === now ? undefined : 'watch'}
    />
  );
}
